import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { getSections, listIdeas } from "../api/ideas";
import IdeaCard from "../components/ideas/IdeaCard";
import IdeaCardSkeleton from "../components/ideas/IdeaCardSkeleton";
import IdeasTopbar from "../components/ideas/IdeasTopbar";
import IdeaLeaderboard from "../components/ideas/IdeaLeaderboard";
import IdeaCreateModal from "../components/ideas/IdeaCreateModal";
import CabinetChrome from "../components/cabinet/CabinetChrome";
import {
    PaletteIcon, ConstructionIcon, FireIcon, SparklesIcon,
    CheckCircleIcon, ArrowRightIcon, PlusIcon,
    BoltIcon, InboxIcon,
} from "../components/ideas/IdeasIcons";
import usePageTitle from "../hooks/usePageTitle";
import "../styles/ideas.css";

/**
 * Главная /ideas — hero (animated mesh) + 4 секции + leaderboard
 * (на десктопе справа, на мобиле — снизу). FAB заменяет hero-CTA на мобиле.
 */
export default function IdeasPage() {
    usePageTitle("Идеи — Рублокс");
    const { isAuthenticated } = useAuth();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [stats, setStats] = useState(null);
    const [err, setErr] = useState(null);
    const [openModal, setOpenModal] = useState(false);

    useEffect(() => {
        const ctl = new AbortController();
        getSections({ signal: ctl.signal })
            .then(setData)
            .catch((e) => { if (e.name !== "AbortError") setErr(e.message); });
        // мини-статы для hero — берём total из /ideas?limit=1
        listIdeas({ limit: 1 }, { signal: ctl.signal })
            .then((r) => setStats({ total: r.total }))
            .catch(() => {});
        return () => ctl.abort();
    }, []);

    const handlePropose = () => {
        if (!isAuthenticated) {
            navigate("/login?back=/ideas");
            return;
        }
        setOpenModal(true);
    };

    return (
        <CabinetChrome>
        <div className="idz-page">
            <IdeasTopbar />

            {/* ── HERO ────────────────────────────────────────────── */}
            <section className="idz-hero">
                <span className="idz-hero-mesh" />
                <div className="idz-container">
                    <div className="idz-hero-grid">
                        <div>
                            <h1>Народный роадмап Рублокса</h1>
                            <p>
                                Предлагай идеи, голосуй за чужие, следи как мы их
                                делаем. За идею, которая попала в игру —
                                {" "}<b>50 алмазов</b> и эксклюзивный бейдж{" "}
                                <PaletteIcon />.
                            </p>
                            {stats && (
                                <div className="idz-hero-stats">
                                    <span className="idz-hero-stat">
                                        <InboxIcon /> <b>{stats.total}</b> идей в роадмапе
                                    </span>
                                    <span className="idz-hero-stat">
                                        <BoltIcon /> <b>50</b> алмазов за реализацию
                                    </span>
                                </div>
                            )}
                        </div>
                        <button
                            className="idz-hero-btn idz-hide-on-mobile"
                            onClick={handlePropose}
                        >
                            <PlusIcon />
                            {isAuthenticated ? "Предложить идею" : "Войти и предложить"}
                        </button>
                    </div>
                </div>
            </section>

            {/* ── основной grid: контент + leaderboard ──────────── */}
            <main className="idz-container">
                <div
                    className="idz-main-grid"
                    style={{ display: "grid", gridTemplateColumns: "1fr 320px", gap: 24 }}
                >
                    <div>
                        {err && <div className="idz-err">{err}</div>}
                        {data == null && !err && (
                            <>
                                <SkelSection title="Сейчас работаем" />
                                <SkelSection title="Топ за неделю" />
                            </>
                        )}
                        {data && (
                            <>
                                <Section
                                    icon={<ConstructionIcon />}
                                    title="Сейчас работаем"
                                    items={data.in_progress}
                                    more="/ideas/list?sort=in_progress"
                                    emptyText="Пока ничего не в разработке"
                                />
                                <Section
                                    icon={<FireIcon />}
                                    title="Топ за неделю"
                                    items={data.top_week}
                                    more="/ideas/list?sort=top"
                                    emptyText="На этой неделе ещё нет лидеров"
                                />
                                <Section
                                    icon={<SparklesIcon />}
                                    title="Новые"
                                    items={data.new}
                                    more="/ideas/list?sort=new"
                                    emptyText="Новых идей пока нет"
                                />
                                <Section
                                    icon={<CheckCircleIcon />}
                                    title="Готово недавно"
                                    items={data.done}
                                    more="/ideas/list?status=done"
                                    emptyText="Скоро здесь появятся завершённые идеи"
                                />
                            </>
                        )}
                    </div>

                    <aside>
                        <div className="idz-paper">
                            <h3 style={{ margin: "0 0 10px", fontSize: 15, fontWeight: 800, letterSpacing: "-0.2px" }}>
                                Топ-5 авторов за год
                            </h3>
                            <IdeaLeaderboard period="year" limit={5} />
                            <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid #f3f4f6" }}>
                                <Link
                                    to="/ideas/list"
                                    style={{ color: "var(--idz-brand)", fontWeight: 600, textDecoration: "none", fontSize: 14 }}
                                >
                                    Смотреть все идеи <ArrowRightIcon />
                                </Link>
                            </div>
                        </div>
                    </aside>
                </div>
            </main>

            {/* FAB для мобилки */}
            <button className="idz-fab" onClick={handlePropose}
                    aria-label="Предложить идею">
                <PlusIcon /> Идея
            </button>

            <IdeaCreateModal open={openModal} onClose={() => setOpenModal(false)} />
        </div>
        </CabinetChrome>
    );
}

function Section({ icon, title, items, more, emptyText }) {
    return (
        <div className="idz-section">
            <div className="idz-section-head">
                <h2>
                    {icon && <span className="idz-sec-ico">{icon}</span>}
                    {title}
                </h2>
                {more && <Link to={more}>Все <ArrowRightIcon /></Link>}
            </div>
            {(!items || items.length === 0) ? (
                <div className="idz-empty">
                    <span className="idz-empty-icon"><InboxIcon /></span>
                    <div>{emptyText}</div>
                </div>
            ) : (
                <div className="idz-row">
                    {items.map((i) => <IdeaCard key={i.id} idea={i} />)}
                </div>
            )}
        </div>
    );
}

function SkelSection({ title }) {
    return (
        <div className="idz-section">
            <div className="idz-section-head">
                <h2>
                    <span className="idz-sec-ico" style={{ opacity: 0.5 }}>
                        <SparklesIcon />
                    </span>
                    {title}
                </h2>
            </div>
            <IdeaCardSkeleton count={3} />
        </div>
    );
}
